/**
 * ``usePlatformFocusFlyTo`` — the platform side of the list "Select on Map"
 * force-recenter (REQ-UI-096; the 2026-06-09 two-slot selection-sync
 * redesign, Phase 2; ADR-024 §5).
 *
 * # Why this is a hook in :file:`Map.tsx`, not an effect in the layer
 *
 * The detection / alert layers resolve their force-recenter coordinates from
 * their **global** stores (``useDetectionsStore`` / ``useAlertsStore``
 * ``byId``), so "Select on Map" recenters to any detection/alert — including
 * one off-screen. The platform layer can't: its only coordinate source is the
 * **viewport-scoped** Map snapshot (``featureCollectionRef``), so a platform
 * absent from the current viewport has no client-side position and the old
 * snapshot-layer force-recenter effect simply no-op'd — defeating the whole
 * point of "Select on Map" (jumping the camera to an *off-screen* platform).
 *
 * The Platforms list (``useProducers`` / ``useProducersStatus``) likewise
 * carries no position. The one **producer-scoped, time-unbounded** position
 * source is ``GET /api/query/producer/{id}/latest-status`` — the same endpoint
 * the Entity Details panel already fetches for the inspected platform. So this
 * hook fetches that (via the shared :func:`fetchProducerLatestStatus`, keyed by
 * :func:`producerLatestStatusQueryKey`, so the call dedupes against / serves
 * from the panel's TanStack Query cache) and eases the camera to the returned
 * coordinates. Because it does I/O + lives above the WebGL layer lifecycle, it
 * belongs in :file:`Map.tsx` alongside the layers rather than inside the
 * snapshot layer's render-only effects.
 *
 * # Behaviour
 *
 *   - Fires only on a focus ``nonce`` change (a repeat "Select on Map" on the
 *     same row recenters again) whose ``target.kind === "platform"`` AND whose
 *     id still matches the current ``mapSelected`` slot (the panel sets both in
 *     one gesture; the guard keeps the camera in sync with the ring).
 *   - A map-glyph click never raises :func:`requestMapFocus`, so it never
 *     force-recenters — the redesign's intended divergence (a click on a
 *     visible marker leaves the camera put).
 *   - Eases **unconditionally** (no in-viewport check): the gesture's whole
 *     point is to make a possibly-off-screen platform the camera focus.
 *   - The ring is **not** set here. After the ease, the viewport change drives
 *     a fresh snapshot that renders the platform, and the snapshot layer's
 *     existing ``setSelectedPlatformHighlight`` (keyed on ``mapSelected``) rings
 *     it once it appears. This hook only moves the camera.
 *   - A platform with no latest status / no position resolves to a no-op
 *     (acceptable — there is no known position to recenter to); a removed /
 *     not-yet-loaded map is a safe no-op (fail-safe, CLAUDE.md §8.1).
 *
 * # Race safety
 *
 * Each fire captures the firing ``nonce``; the async result is applied only if
 * (a) the effect hasn't been superseded by a newer nonce / unmounted (a local
 * ``cancelled`` flag flipped in cleanup) and (b) the map is still usable. A
 * rapid second "Select on Map" therefore can't let a stale fetch ease the
 * camera to the previous target.
 *
 * Cross-references:
 *   - REQ-UI-096 (cross-pane "Select on Map") / REQ-UI-051 (two-slot bus) /
 *     REQ-UI-027 (Zustand client state) / REQ-UI-026 (TanStack Query server
 *     state).
 *   - ADR-024 §5 (selection contract — 200 ms ease; force-recenter vs
 *     pan-if-not-in-view).
 *   - selection/map-focus-store.ts (the one-shot focus signal) /
 *     selection/selection-store.ts (the ``mapSelected`` slot).
 *   - entity-details/use-producer-latest-status.ts (the shared fetch + key).
 *   - map/snapshot-layer.tsx (where the viewport-bound force-recenter used to
 *     live — removed in favour of this).
 *   - docs/engineering/detail/storyboards/flows/Q-side-by-side-map-platforms/README.md.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Map as MapLibreMap } from "maplibre-gl";
import { useMapSelectedIdForKind } from "@/selection/selection-store";
import { useMapFocusSignal } from "@/selection/map-focus-store";
import { isMapUsable } from "@/map/map-ready";
import { focusZoom } from "@/map/cluster-styling";
import {
  fetchProducerLatestStatus,
  producerLatestStatusQueryKey,
  PRODUCER_LATEST_STATUS_REFETCH_MS,
} from "@/entity-details/use-producer-latest-status";

/**
 * Camera ease used when force-recentering to a platform via the list "Select
 * on Map" affordance. Per ADR-024 §5 / REQ-UI-051 (200 ms ease) — the same
 * value the detection / alert pans use. Relocated here from the (removed)
 * snapshot-layer force-recenter effect; the snapshot layer itself never pans.
 */
export const PLATFORM_PAN_DURATION_MS = 200;

/**
 * Watch the map-focus signal and, on a platform-kind "Select on Map" request,
 * fetch the platform's latest position and ease the camera to it.
 *
 * Mount once in :file:`Map.tsx` with the loaded map (``null`` before the
 * map's ``load`` event — a no-op until then). Renders nothing.
 *
 * @param map - The loaded MapLibre map, or ``null`` before load / after
 *   teardown.
 *
 * Cross-references:
 *   - REQ-UI-096; REQ-UI-051; ADR-024 §5.
 */
export function usePlatformFocusFlyTo(map: MapLibreMap | null): void {
  const queryClient = useQueryClient();
  // The MAP selection slot (the two-slot redesign): the focus target must
  // still match this for the camera to move (keeps the ease in sync with the
  // ring the snapshot layer draws off ``mapSelected``).
  const selectedPlatformId = useMapSelectedIdForKind("platform");
  // The one-shot force-recenter signal — only the Platforms-panel "Select on
  // Map" row menu raises it; a glyph click never does (the divergence).
  const focusSignal = useMapFocusSignal();

  useEffect(() => {
    if (focusSignal.nonce === 0) {
      return; // No focus requested yet.
    }
    const target = focusSignal.target;
    if (target === null || target.kind !== "platform") {
      return; // Not a platform focus request (detection/alert handle their own).
    }
    if (target.id !== selectedPlatformId) {
      return; // selectOnMap sets mapSelected in the same gesture; stay in sync.
    }
    if (map === null || !isMapUsable(map)) {
      return; // Null / removed map (e.g., "Reset to profile" teardown) — drop.
    }
    // ``map`` is non-null here; bind it so the async closure (which TS won't
    // narrow across the ``await``) keeps the non-null type.
    const liveMap: MapLibreMap = map;
    const producerId = target.id;

    // Guard the async result against a superseding request / unmount.
    let cancelled = false;
    void (async () => {
      let response: Awaited<ReturnType<typeof fetchProducerLatestStatus>>;
      try {
        response = await queryClient.fetchQuery({
          queryKey: producerLatestStatusQueryKey(producerId),
          queryFn: ({ signal }) => fetchProducerLatestStatus(producerId, signal),
          // Honour the panel's freshness window so a recent fetch is served
          // from cache rather than re-hitting the BFF (REQ-UI-035 stays a
          // single same-origin call either way).
          staleTime: PRODUCER_LATEST_STATUS_REFETCH_MS,
        });
      } catch {
        // A failed latest-status fetch (e.g., transient BFF error) leaves the
        // camera put — there is no known position to recenter to. The panel
        // still set both selection slots; the operator can retry. Swallowed
        // rather than thrown so an unhandled rejection never escapes the
        // detached async task (the error surfaced via the panel's own query).
        return;
      }
      if (cancelled || !isMapUsable(liveMap)) {
        return; // Superseded by a newer focus / unmounted / map torn down.
      }
      if (response === null) {
        return; // Producer has no status yet → no position to recenter to.
      }
      const { latitude_deg, longitude_deg } = response.status;
      if (latitude_deg === null || longitude_deg === null) {
        // The platform reported status but no node location (REQ-INGEST-044) —
        // there is no coordinate to recenter to, so no-op the fly-to. The
        // Entity Details STATUS panel still populates; the platform just has no
        // map marker to land the ring on.
        return;
      }
      // Zoom IN to un-cluster (REQ-UI-096): a platform picked from the list
      // while the map is at cluster-level zoom is hidden inside a bubble, so
      // ease to ``focusZoom`` (≥ CLUSTER_MAX_ZOOM + 1) where it paints as an
      // individual marker the snapshot-layer ring can land on; an already-
      // zoomed-in map keeps its zoom. Read the zoom at apply time (after the
      // await) so a concurrent gesture doesn't compute a stale target.
      liveMap.easeTo({
        center: [longitude_deg, latitude_deg],
        zoom: focusZoom(liveMap.getZoom()),
        duration: PLATFORM_PAN_DURATION_MS,
      });
    })();

    return () => {
      cancelled = true;
    };
    // Keyed on ``nonce`` (the changing signal) + ``map`` / ``queryClient`` (the
    // stable instances). ``selectedPlatformId`` and the focus ``target`` are
    // read at fire time — only a fresh request (nonce bump) should recenter,
    // not a snapshot/selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, queryClient, focusSignal.nonce]);
}
